import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CRITICAL_RESULTS_FILTER_QUERY_KEYS,
  CRITICAL_RESULTS_LIST_PAGE_SIZE,
} from '../constants/criticalResultsDashboard.constants';
import type { CriticalResultsSortField } from '../constants/criticalResultsDashboard.constants';
import type { CriticalResultsDashboardFilters } from '../types/criticalResultsDashboard.types';
import type { CriticalityBand } from '../types/escalationMatrix.types';
import type { EscalationInstanceStatus } from '../types/escalationRuntime.types';
import {
  isoDateEnd,
  isoDateStart,
  monthStartIsoDate,
  todayIsoDate,
} from '../utils/criticalResultsDashboardUtils';

export interface CriticalResultsFilterState {
  startDate: string;
  endDate: string;
  facilityId: number | null;
  department: string;
  criticalityBand: string;
  status: string;
  escalationLevel: string;
  referringClinician: string;
  autoRefresh: boolean;
}

export interface CriticalResultsListState {
  page: number;
  sortBy: CriticalResultsSortField;
  sortOrder: 'ASC' | 'DESC';
  search: string;
}

export function createDefaultFilterState(
  defaultFacilityId: number | null
): CriticalResultsFilterState {
  return {
    startDate: monthStartIsoDate(),
    endDate: todayIsoDate(),
    facilityId: defaultFacilityId,
    department: '',
    criticalityBand: '',
    status: '',
    escalationLevel: '',
    referringClinician: '',
    autoRefresh: true,
  };
}

function parseBooleanParam(value: string | null, fallback: boolean): boolean {
  if (value == null || value === '') {
    return fallback;
  }
  return value === '1' || value.toLowerCase() === 'true';
}

function parsePositiveIntParam(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export function parseFilterStateFromSearchParams(
  searchParams: URLSearchParams,
  defaultFacilityId: number | null
): CriticalResultsFilterState {
  const defaults = createDefaultFilterState(defaultFacilityId);
  const facilityParam = searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.facilityId);
  const parsedFacility = facilityParam != null ? Number(facilityParam) : NaN;

  return {
    startDate: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.startDate) || defaults.startDate,
    endDate: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.endDate) || defaults.endDate,
    facilityId:
      Number.isInteger(parsedFacility) && parsedFacility > 0 ? parsedFacility : defaults.facilityId,
    department: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.department) || '',
    criticalityBand: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.criticalityBand) || '',
    status: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.status) || '',
    escalationLevel: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.escalationLevel) || '',
    referringClinician:
      searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.referringClinician) || '',
    autoRefresh: parseBooleanParam(
      searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.autoRefresh),
      defaults.autoRefresh
    ),
  };
}

export function parseListStateFromSearchParams(
  searchParams: URLSearchParams,
  defaults: Pick<CriticalResultsListState, 'sortBy' | 'sortOrder'>
): CriticalResultsListState {
  const sortByParam = searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.sortBy);
  const sortOrderParam = searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.sortOrder);
  const sortBy =
    sortByParam &&
    (
      [
        'startedAt',
        'acknowledgedAt',
        'nextActionAt',
        'currentLevelNumber',
        'criticalityBand',
        'status',
      ] as const
    ).includes(sortByParam as CriticalResultsSortField)
      ? (sortByParam as CriticalResultsSortField)
      : defaults.sortBy;

  return {
    page: parsePositiveIntParam(searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.page), 1),
    sortBy,
    sortOrder: sortOrderParam?.toUpperCase() === 'ASC' ? 'ASC' : defaults.sortOrder,
    search: searchParams.get(CRITICAL_RESULTS_FILTER_QUERY_KEYS.search) || '',
  };
}

export function appendFilterStateToSearchParams(
  params: URLSearchParams,
  filters: CriticalResultsFilterState
) {
  const setOrDelete = (key: string, value: string | number | boolean | null | undefined) => {
    if (value == null || value === '' || value === false) {
      params.delete(key);
      return;
    }
    params.set(key, String(value));
  };

  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.startDate, filters.startDate);
  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.endDate, filters.endDate);
  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.facilityId, filters.facilityId);
  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.department, filters.department);
  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.criticalityBand, filters.criticalityBand);
  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.status, filters.status);
  setOrDelete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.escalationLevel, filters.escalationLevel);
  setOrDelete(
    CRITICAL_RESULTS_FILTER_QUERY_KEYS.referringClinician,
    filters.referringClinician.trim()
  );
  if (filters.autoRefresh) {
    params.set(CRITICAL_RESULTS_FILTER_QUERY_KEYS.autoRefresh, '1');
  } else {
    params.delete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.autoRefresh);
  }
  params.delete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.refreshInterval);
}

export function appendListStateToSearchParams(
  params: URLSearchParams,
  list: CriticalResultsListState
) {
  if (list.page > 1) {
    params.set(CRITICAL_RESULTS_FILTER_QUERY_KEYS.page, String(list.page));
  } else {
    params.delete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.page);
  }

  params.set(CRITICAL_RESULTS_FILTER_QUERY_KEYS.sortBy, list.sortBy);
  params.set(CRITICAL_RESULTS_FILTER_QUERY_KEYS.sortOrder, list.sortOrder);

  if (list.search.trim()) {
    params.set(CRITICAL_RESULTS_FILTER_QUERY_KEYS.search, list.search.trim());
  } else {
    params.delete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.search);
  }
}

export function buildApiFiltersFromState(
  filters: CriticalResultsFilterState
): CriticalResultsDashboardFilters | null {
  if (!filters.facilityId) {
    return null;
  }

  let startDate = filters.startDate;
  let endDate = filters.endDate;
  if (startDate && endDate && startDate > endDate) {
    [startDate, endDate] = [endDate, startDate];
  }

  const escalationLevel = filters.escalationLevel ? Number(filters.escalationLevel) : undefined;

  return {
    facilityId: filters.facilityId,
    criticalityBand: (filters.criticalityBand || undefined) as CriticalityBand | undefined,
    status: (filters.status || undefined) as EscalationInstanceStatus | undefined,
    escalationLevel:
      escalationLevel !== undefined && Number.isInteger(escalationLevel) && escalationLevel > 0
        ? escalationLevel
        : undefined,
    department: filters.department || undefined,
    referringClinician: filters.referringClinician.trim() || undefined,
    startedAfter: isoDateStart(startDate),
    startedBefore: isoDateEnd(endDate),
  };
}

export function useCriticalResultsFilters(defaultFacilityId: number | null) {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(
    () => parseFilterStateFromSearchParams(searchParams, defaultFacilityId),
    [searchParams, defaultFacilityId]
  );

  const apiFilters = useMemo(() => buildApiFiltersFromState(filters), [filters]);

  const updateFilters = useCallback(
    (partial: Partial<CriticalResultsFilterState>, options?: { resetPage?: boolean }) => {
      setSearchParams(
        current => {
          const nextParams = new URLSearchParams(current);
          const nextFilters = { ...filters, ...partial };
          appendFilterStateToSearchParams(nextParams, nextFilters);
          if (options?.resetPage) {
            nextParams.delete(CRITICAL_RESULTS_FILTER_QUERY_KEYS.page);
          }
          return nextParams;
        },
        { replace: true }
      );
    },
    [filters, setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      () => {
        const nextParams = new URLSearchParams();
        appendFilterStateToSearchParams(nextParams, createDefaultFilterState(defaultFacilityId));
        return nextParams;
      },
      { replace: true }
    );
  }, [defaultFacilityId, setSearchParams]);

  const buildDrillDownSearch = useCallback(
    (extra?: Partial<CriticalResultsListState>) => {
      const params = new URLSearchParams();
      appendFilterStateToSearchParams(params, filters);
      if (extra) {
        appendListStateToSearchParams(params, {
          page: extra.page ?? 1,
          sortBy: extra.sortBy ?? 'startedAt',
          sortOrder: extra.sortOrder ?? 'DESC',
          search: extra.search ?? '',
        });
      }
      const qs = params.toString();
      return qs ? `?${qs}` : '';
    },
    [filters]
  );

  return {
    filters,
    apiFilters,
    updateFilters,
    clearFilters,
    buildDrillDownSearch,
    searchParams,
    setSearchParams,
  };
}

export function useCriticalResultsListParams(
  defaults: Pick<CriticalResultsListState, 'sortBy' | 'sortOrder'>
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultSortBy = defaults.sortBy;
  const defaultSortOrder = defaults.sortOrder;

  const listState = useMemo(
    () =>
      parseListStateFromSearchParams(searchParams, {
        sortBy: defaultSortBy,
        sortOrder: defaultSortOrder,
      }),
    [searchParams, defaultSortBy, defaultSortOrder]
  );

  const updateListState = useCallback(
    (partial: Partial<CriticalResultsListState>) => {
      setSearchParams(
        current => {
          const nextParams = new URLSearchParams(current);
          const nextList = { ...listState, ...partial };
          appendListStateToSearchParams(nextParams, nextList);
          return nextParams;
        },
        { replace: true }
      );
    },
    [listState, setSearchParams]
  );

  return {
    listState,
    updateListState,
    pageSize: CRITICAL_RESULTS_LIST_PAGE_SIZE,
  };
}
